import { subYears } from "date-fns";

import { Company } from "../../setups/types/setupTypes";
import {
  countryFromOffice,
  foreignAgentPartnersFromSource,
  OfficePartnerSource,
  resolveOfficeFromUserPartners,
  resolveOwnerForForeignOffice,
  resolveParentCompanyForOffice,
} from "../../process/utils/officePartners";
import {
  Employee,
  EmployeeStatus,
  Nationalities,
  Sex,
} from "../../process/types/processTypes";

export const DEFAULT_CALLING_SEX = Sex.female;
export const DEFAULT_CALLING_NATIONALITY = Nationalities.ethiopian;

export function buildCallingInitialValues(
  selectedEmployee: Employee | null | undefined,
  partnerSource: OfficePartnerSource,
): Employee {
  const partners = foreignAgentPartnersFromSource(partnerSource);
  const defaultPartner = partners[0];
  const defaultCountry = countryFromOffice(defaultPartner);

  if (selectedEmployee?._id) {
    const resolvedPartner =
      resolveOfficeFromUserPartners(selectedEmployee.partner, partnerSource) ??
      defaultPartner;

    return {
      ...selectedEmployee,
      calling: true,
      status: selectedEmployee.status ?? EmployeeStatus.selected,
      sex: selectedEmployee.sex ?? DEFAULT_CALLING_SEX,
      dateOfBirth: selectedEmployee.dateOfBirth
        ? new Date(selectedEmployee.dateOfBirth)
        : subYears(new Date(), 25),
      partner: resolvedPartner,
      address: {
        ...selectedEmployee.address,
        telephone: selectedEmployee.address?.telephone ?? "",
      },
      contactPerson: {
        ...selectedEmployee.contactPerson,
        displayName: selectedEmployee.contactPerson?.displayName ?? "",
        address: {
          ...selectedEmployee.contactPerson?.address,
          telephone:
            selectedEmployee.contactPerson?.address?.telephone ?? "",
        },
      },
      additionalData: {
        ...selectedEmployee.additionalData,
        nationality:
          selectedEmployee.additionalData?.nationality ??
          DEFAULT_CALLING_NATIONALITY,
        appliedCountry: countryFromOffice(resolvedPartner),
      },
      education: {
        ...selectedEmployee.education,
        skills: selectedEmployee.education?.skills ?? [],
      },
    } as Employee;
  }

  return {
    status: EmployeeStatus.selected,
    calling: true,
    sex: DEFAULT_CALLING_SEX,
    dateOfBirth: subYears(new Date(), 25),
    givenName: "",
    surName: "",
    passportNumber: "",
    partner: defaultPartner,
    address: { telephone: "" },
    contactPerson: {
      displayName: "",
      address: { telephone: "" },
    },
    additionalData: {
      nationality: DEFAULT_CALLING_NATIONALITY,
      appliedCountry: defaultCountry,
    },
    education: {
      skills: [],
    },
  } as Employee;
}

export function buildCallingSavePayload(
  values: Employee,
  options: {
    selectedEmployee?: Employee | null;
    partnerSource: OfficePartnerSource;
    user?: { company?: Company };
  },
): Employee {
  const { selectedEmployee, partnerSource, user } = options;
  const office = resolveOfficeFromUserPartners(values.partner, partnerSource);
  const owner =
    resolveOwnerForForeignOffice(office, partnerSource) ?? user?.company;
  const parentCompany = resolveParentCompanyForOffice(owner, partnerSource);

  let payload: Employee = {
    ...values,
    _id: values._id ?? selectedEmployee?._id,
    calling: true,
    partner: office ?? values.partner,
    sex: values.sex ?? DEFAULT_CALLING_SEX,
    additionalData: {
      ...values.additionalData,
      nationality:
        values.additionalData?.nationality ?? DEFAULT_CALLING_NATIONALITY,
      appliedCountry: countryFromOffice(office),
    },
  };

  if (owner) {
    payload = { ...payload, owner };
  }
  if (parentCompany) {
    payload = { ...payload, parentCompany };
  }

  return payload;
}

export function syncCallingOfficeFields(
  office: Company | undefined,
  partnerSource: OfficePartnerSource,
  currentAdditionalData: Employee["additionalData"],
): { partner: Company | undefined; additionalData: Employee["additionalData"] } {
  const resolved = resolveOfficeFromUserPartners(office, partnerSource);
  return {
    partner: resolved,
    additionalData: {
      ...currentAdditionalData,
      nationality:
        currentAdditionalData?.nationality ?? DEFAULT_CALLING_NATIONALITY,
      appliedCountry: countryFromOffice(resolved),
    },
  };
}
